import useUser from '../../../../utility/hooks/useUser';
import PropTypes from 'prop-types';
import React from 'react';
import ReadOnlyNotice from './ReadOnlyNotice';

const permissions = {
    hide: 0,
    view: 1,
    edit: 2,
};

// A parent's View, which also shows the 'View only' pop-up.
const parentViewOnly = 'parentView';

// The access a person has to one section of a child's profile. A parent's own permissions are their parent
// permissions, so a parent of this child with View access is given the parent's View, which shows the 'View only'
// pop-up, before the plain View (Maria, 17 October 2026; the pop-up was never reached for parents before this).
export const sectionAccess = (user, permissionModule, isParentOfStudent) => {
    if (permissionModule === '*') {
        return permissions.edit;
    }

    const own = user.permissions ?? [];
    const asParent = isParentOfStudent ? (user.parentPermissions ?? []) : [];

    if (own.includes(`Permission.${permissionModule}.Edit`) || asParent.includes(`Permission.${permissionModule}.Edit`)) {
        return permissions.edit;
    }

    if (isParentOfStudent && (asParent.includes(`Permission.${permissionModule}.View`) ||
        own.includes(`Permission.${permissionModule}.View`))) {
        return parentViewOnly;
    }

    if (own.includes(`Permission.${permissionModule}.View`)) {
        return permissions.view;
    }

    return permissions.hide;
};

const PermissionCheck = ({permissionModule, render, returnType = 'readonly', studentProfile = null}) => {
    const {user} = useUser();

    const hasPermission = () => sectionAccess(user, permissionModule, Boolean(studentProfile && studentProfile.isParentOfStudent));

    if (returnType === 'readonly') {
        return {
            // Maria, 17 October 2026: a parent who can only view this section sees the 'View only' pop-up above it.
            [parentViewOnly]: () => <>{<ReadOnlyNotice/>}{render(true)}</>,
            [permissions.view]: () => render(true),
            [permissions.edit]: () => render(false),
            [permissions.hide]: () => null
        }[hasPermission()]();
    } else if (returnType === 'isEdit') {
        return hasPermission() === permissions.edit ? render(true) : render(false);
    } else {
        return null;
    }

};

PermissionCheck.propTypes = {
    permissionModule: PropTypes.string.isRequired,
    render: PropTypes.func.isRequired,
    returnType: PropTypes.oneOf(['readonly', 'isEdit']),
    studentProfile: PropTypes.shape({
        isParentOfStudent: PropTypes.bool
    })
};

export default PermissionCheck;